import { FlatAspect } from "@/models/HomePageSearchResponse";
import { Box, Divider, styled, Typography } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AspectMenu from "./AspectMenu";
import _ from "lodash";
import { AspectDivider } from "./AspectDivider";

export const LANDSCAPE_MAX_WIDTH = "md";

function SingleOption({
  option,
  selectedValues,
  onChange,
}: {
  option: FlatAspect;
  selectedValues: FlatAspect[];
  onChange: (selectedValues: FlatAspect[], keepOpen: boolean) => void;
}) {
  const isSelected = selectedValues.some((x) => x.id === option.id);
  const newSelectedValues = isSelected
    ? selectedValues.filter((x) => x.id !== option.id)
    : [...selectedValues, option];

  return (
    <Option
      isSelected={isSelected}
      option={option}
      onClick={(keepOpen) => onChange(newSelectedValues, keepOpen)}
    />
  );
}

function Option({
  isSelected,
  option,
  onClick,
}: {
  isSelected: boolean;
  option: { id: string; label: string; count?: number };
  onClick: (keepOpen: boolean) => void;
}) {
  return (
    <Box
      key={option.id}
      sx={(theme) => ({
        cursor: "pointer",
        "@media (hover: hover)": {
          "&:hover": {
            backgroundColor: "grey.200",
          },
        },
        display: "flex",
        gap: 1,
        padding: theme.spacing(0.5, 2),
        alignItems: "center",
      })}
      onClick={() => onClick(true)}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {isSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
      </Box>
      <Typography className="option-label">{option.label}</Typography>
      <Typography variant="caption" sx={{ color: "grey.700" }}>
        ({option.count})
      </Typography>
    </Box>
  );
}

function OptionsList({
  options,
  selectedValues,
  onChange,
}: {
  options: FlatAspect[];
  selectedValues: FlatAspect[];
  onChange: (selectedValues: FlatAspect[], keepOpen: boolean) => void;
}) {
  const groups = _.uniqBy(
    options.map((x) => ({ id: x.groupId, label: x.groupLabel })),
    (x) => x.id,
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {groups.map((group) => {
        return (
          <Box
            key={group.id}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <MenuTitle>{group.label}</MenuTitle>

            <MenuSectionBody>
              {options
                .filter((x) => x.groupId === group.id)
                .map((option) => {
                  return (
                    <SingleOption
                      key={option.id}
                      option={option}
                      selectedValues={selectedValues}
                      onChange={onChange}
                    />
                  );
                })}
            </MenuSectionBody>
          </Box>
        );
      })}
    </Box>
  );
}

const MenuSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  paddingBottom: theme.spacing(2),
}));

const MenuSectionBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexWrap: "wrap",
  maxHeight: "500px",
  columnGap: 6,
  [theme.breakpoints.down(LANDSCAPE_MAX_WIDTH)]: {
    flexWrap: "nowrap",
  },
}));

const MenuTitle = function MenuTitle({
  children,
}: {
  children: React.ReactNode;
}) {
  return children ? (
    <Typography
      variant="h6"
      sx={{
        padding: 2,
      }}
    >
      {children}
    </Typography>
  ) : null;
};

export default function AspectMultiMenu({
  title,
  label,
  options,
  selectedValues,
  onChange,
  icon,
  topSlot,
  hideOptions,
}: {
  title: string;
  label: string;
  options: FlatAspect[];
  selectedValues: FlatAspect[];
  onChange: (selectedValues: FlatAspect[], skipRefresh: boolean) => void;
  icon: React.ReactNode;
  topSlot?: React.ReactNode;
  hideOptions?: boolean;
}) {
  const topOptions = options.filter((x) => x.isTop);

  return (
    <>
      {(options.length > 0 || topSlot) && (
        <>
          <AspectDivider />
          <AspectMenu
            label={label}
            icon={icon}
            selectedValues={selectedValues.map((x) => x.label)}
            onClear={() => onChange([], false)}
            body={(handleClose) => {
              const handleOnChange = (
                newSelectedValues: FlatAspect[],
                keepOpen: boolean,
              ) => {
                onChange(newSelectedValues, keepOpen);
                if (!keepOpen) {
                  handleClose();
                }
              };
              return (
                <Box>
                  {topSlot}
                  <Box
                    sx={(theme) => ({
                      display: "flex",
                      [theme.breakpoints.down(LANDSCAPE_MAX_WIDTH)]: {
                        flexDirection: "column",
                      },
                    })}
                  >
                    {!hideOptions && (
                      <>
                        {topOptions.length > 0 && (
                          <>
                            <MenuSection>
                              <MenuTitle>Top {label}s</MenuTitle>
                              <OptionsList
                                options={topOptions}
                                selectedValues={selectedValues}
                                onChange={handleOnChange}
                              />
                            </MenuSection>
                            <Divider orientation="vertical" flexItem />
                          </>
                        )}
                        <MenuSection>
                          {title && (
                            <MenuTitle>
                              {topOptions.length > 0 ? "All " : ""}
                              {title}
                            </MenuTitle>
                          )}
                          <OptionsList
                            // AJB: better to avoid duplicating on the server?
                            options={options.filter((x) => !x.isTop)}
                            selectedValues={selectedValues}
                            onChange={handleOnChange}
                          />
                        </MenuSection>
                      </>
                    )}
                  </Box>
                </Box>
              );
            }}
          />
        </>
      )}
    </>
  );
}
